import { useMemo } from 'react';
import { useMapSalesRuleToCouponType } from './useMapSalesRuleToCouponType';
import { useMapCouponContent } from './useMapCouponContent';
import type { CouponContentResponse, SalesRule } from '../../../types/front.graphql';
import { getCouponImageUri } from '@kidslabs/module-core';
type CouponDataType = SalesRule | CouponContentResponse;

type DataType = 'salesRule' | 'couponContent';

const isSalesRule = (dataType: DataType, coupon: CouponDataType): coupon is SalesRule => {
    return dataType === 'salesRule' && 'usage_limit' in coupon && 'usage_per_customer' in coupon;
};

export const useMapCouponData = (dataType: DataType, coupon: CouponDataType) => {
    const salesRuleMappers = useMapSalesRuleToCouponType();
    const couponContentMappers = useMapCouponContent();

    const { couponData, contentData } = useMemo(() => {
        if (isSalesRule(dataType, coupon)) {
            return {
                couponData: salesRuleMappers.mapSalesRuleToCouponType(coupon),
                contentData: salesRuleMappers.mapSalesRuleToContent(coupon),
            };
        }

        return {
            couponData: couponContentMappers.mapContentToCouponInfo(coupon),
            contentData: couponContentMappers.mapContentToCouponDetails(coupon),
        };
    }, [dataType, coupon, salesRuleMappers, couponContentMappers]);

    const couponInfo = useMemo(() => {
        const imageUri = isSalesRule(dataType, coupon) ? (couponData?.brand_image ?? '') : getCouponImageUri(coupon);

        return {
            ...couponData,
            brand_image: imageUri,
            usage_limit: couponData?.usage_limit ?? 0,
            usage_per_customer: couponData?.usage_per_customer ?? 0,
            name: couponData?.name ?? coupon?.name ?? '',
        };
    }, [couponData, coupon, dataType]);

    return { couponInfo, contentData };
};
